import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CUSTOM_LANDING_PAGE_OPTION_ID,
  CopyDataList,
} from '@pages/IntroducingBroker/Dashboard/ReferralLink/ReferralLink'
import { LinkProps } from '@pages/IntroducingBroker/LinkHub/Links/types'

import { useSnackbar } from '@context/SnackbarContext'

import { useApiClient } from '@utils/ApiClient'
import { useGAEvents } from '@utils/callGAEvents'
import { ClientApiClient } from '@utils/clientApi'

import { IntroducingBrokerReferralTypes } from '@model/IntroducingBrokerDetailsDto'
import { NameDto } from '@model/NameDto'

interface UseLinkProps extends LinkProps {
  isCustomType?: boolean
  isLinkHub?: boolean
}

export const useLinkHub = ({
  ibCode,
  mtCode,
  ibLink,
  mtLink,
  fetchShortenedIBLink,
  isCustomType = false,
  isLinkHub = true,
}: UseLinkProps) => {
  const { callGAEvents } = useGAEvents()
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const { addSnackbar } = useSnackbar()

  const domain = window.location.host

  const [shortenedMainReferralLink, setShortenedMainReferralLink] = useState<string>()
  const [shortenedMTReferralLink, setShortenedMTReferralLink] = useState<string>()
  const [isShortMainLinkLoading, setIsShortMainLinkLoading] = useState(false)
  const [isShortMTLinkLoading, setIsShortMTLinkLoading] = useState(false)

  const [landingPageUrl, setLandingPageUrl] = useState<NameDto<string>>()
  const [customLandingPageUrl, setCustomLandingPageUrl] = useState<string | null>(null)
  const [mainReferralLink, setMainReferralLink] = useState<string | null | undefined>(
    !isCustomType ? ibLink : undefined
  )
  const [mtReferralLink, setMTReferralLink] = useState<string | null | undefined>(
    !isCustomType ? mtLink : undefined
  )

  const { data: landingPages, isLoading: isLoadingLpUrlsFromCategories } = useQuery({
    queryKey: queryKeys.ib.referralMaterialsUrls(),
    queryFn: () => apiClient.getReferralMaterialsUrls(),
  })

  const { data: previewLandingPages, isLoading: isLoadingPreviewLpUrlsFromCategories } = useQuery({
    queryKey: queryKeys.ib.referralMaterialsPreviewUrls(),
    queryFn: () => apiClient.getReferralMaterialsPreviewUrls(),
  })

  const ibCodes = useMemo<CopyDataList[]>(() => {
    return [
      ...(ibCode?.length
        ? [
            {
              referralType: IntroducingBrokerReferralTypes.Default,
              name: t('IB.Dashboard.IB code'),
              value: ibCode,
              onCopyGAEventClick: () => callGAEvents().IBDashboard.ReferralLink.copyIBCode(),
            },
          ]
        : []),
      ...(mtCode?.length
        ? [
            {
              referralType: IntroducingBrokerReferralTypes.MultiTier,
              name: t('IB.Dashboard.Multi-tier IB code'),
              value: mtCode,
              onCopyGAEventClick: () => callGAEvents().IBDashboard.ReferralLink.copyMTIBCode(),
            },
          ]
        : []),
    ]
  }, [ibCode, mtCode, t, callGAEvents])

  const isCustomLpUrlSelected =
    landingPageUrl?.id === CUSTOM_LANDING_PAGE_OPTION_ID && !!customLandingPageUrl

  function isReferralLinkLoading(isLoading: boolean) {
    setIsShortMainLinkLoading(isLoading)
  }

  useEffect(() => {
    if (!isCustomType) {
      ibLink && setMainReferralLink(ibLink)
      mtLink && isLinkHub && setMTReferralLink(mtLink)
    }
  }, [ibLink, mtLink, customLandingPageUrl, isLinkHub, isCustomType])

  useEffect(() => {
    ;(async () => {
      isReferralLinkLoading(true)

      const introducingBrokerCode = ibCode || mtCode
      const canFetchUrl =
        introducingBrokerCode &&
        landingPageUrl &&
        (landingPageUrl.id !== CUSTOM_LANDING_PAGE_OPTION_ID || isCustomLpUrlSelected)

      try {
        if (canFetchUrl) {
          const response = await apiClient.getReferralUrlByCategory({
            ibCode: introducingBrokerCode,
            isCustomUrl: isCustomLpUrlSelected,
            urlId: isCustomLpUrlSelected ? customLandingPageUrl : landingPageUrl.id,
            domain,
          })

          setMainReferralLink(response.url)

          if (response.url) {
            if (ibCode) {
              const urlObj = new URL(response.url)
              urlObj.searchParams.set('utm_content', ibCode)
              setMainReferralLink(urlObj.toString())
            }

            if (mtCode && isLinkHub) {
              const urlObj = new URL(response.url)
              urlObj.searchParams.set('utm_content', mtCode)
              setMTReferralLink(urlObj.toString())
            }
          }

          addSnackbar.success({ message: t('Validation.Link was successfully generated') })
        } else if (!isCustomLpUrlSelected && !isCustomType) {
          ibLink && setMainReferralLink(ibLink)
          mtLink && isLinkHub && setMTReferralLink(mtLink)
        }
      } finally {
        isReferralLinkLoading(false)
      }
    })()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingPageUrl, customLandingPageUrl, ibCode, mtCode, ibLink, mtLink])

  useEffect(() => {
    mainReferralLink && setShortenedMainReferralLink('')
  }, [mainReferralLink])

  useEffect(() => {
    mtReferralLink && setShortenedMTReferralLink('')
  }, [mtReferralLink])

  const isShorteningAvailable = useMemo(() => {
    const isMainIBShorteningAvailable = !!(
      isCustomLpUrlSelected ||
      (landingPageUrl && landingPageUrl?.id !== CUSTOM_LANDING_PAGE_OPTION_ID) ||
      mainReferralLink
    )

    const isMTIBShorteningAvailable = !!mtLink

    return {
      canShortenMainIB: isMainIBShorteningAvailable,
      canShortenMTIB: isMTIBShorteningAvailable,
    }
  }, [isCustomLpUrlSelected, landingPageUrl, mainReferralLink, mtLink])

  const handleIBLinkShortening = async (referralCode: string) => {
    const isDefaultIB = referralCode === ibCode
    const isMTCode = referralCode === mtCode

    try {
      if (
        (!isShorteningAvailable.canShortenMainIB || !isDefaultIB) &&
        (!isShorteningAvailable.canShortenMTIB || !isMTCode)
      ) {
        return
      }

      isDefaultIB ? setIsShortMainLinkLoading(true) : setIsShortMTLinkLoading(true)

      const isShorteningAllowed =
        !isLinkHub &&
        !isMTCode &&
        (isCustomLpUrlSelected ||
          (landingPageUrl?.id && landingPageUrl?.id !== CUSTOM_LANDING_PAGE_OPTION_ID))

      let response
      if (isShorteningAllowed) {
        const url = isCustomLpUrlSelected ? customLandingPageUrl : landingPageUrl?.id
        response = await apiClient.getReferralUrlByCategory({
          ibCode: referralCode,
          isCustomUrl: isCustomLpUrlSelected,
          urlId: url ?? '',
          generateShortenUrl: true,
          domain,
        })
      } else {
        const { shortUrl } = await fetchShortenedIBLink(referralCode, true)
        response = { shortenUrl: shortUrl }
      }

      if (response.shortenUrl) {
        isDefaultIB
          ? setShortenedMainReferralLink(response.shortenUrl)
          : setShortenedMTReferralLink(response.shortenUrl)
        addSnackbar.success({ message: t('Validation.Link was successfully generated') })
      } else {
        addSnackbar.info({
          message: t('Something went wrong! Please try again later'),
        })
      }

      callGAEvents().IBDashboard.ReferralLink.shortenLink()
    } finally {
      setIsShortMainLinkLoading(false)
      setIsShortMTLinkLoading(false)
    }
  }

  const resetReferralLinks = () => {
    setMainReferralLink(undefined)
    setMTReferralLink(undefined)
  }

  return {
    ibCodes,
    links: {
      mainReferralLink,
      mtReferralLink,
      resetReferralLinks,
    },
    shortening: {
      shortenedMainReferralLink,
      shortenedMTReferralLink,
      setShortenedMainReferralLink,
      isShortMainLinkLoading,
      isShortMTLinkLoading,
      isShorteningAvailable,
      handleIBLinkShortening,
    },
    landingPages: {
      landingPageUrl,
      setLandingPageUrl,
      customLandingPageUrl,
      setCustomLandingPageUrl,
      landingPages,
      previewLandingPages,
      isLoadingLpUrlsFromCategories,
      isLoadingPreviewLpUrlsFromCategories,
      isCustomLpUrlSelected,
    },
  }
}
